import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronDown, faPlus, faSearch, faXmark, faPaperPlane, faPlay, faAward, faTruck, faEnvelope, faLink } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faWhatsapp, faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

// Code-built stand-ins for app screens whose real screenshots would need
// copyrighted posters, real people or content we don't have. They render inside the always-dark
// feature frame, so they only use colors the light-mode overrides in
// tailwind.css don't remap. The accent is written in lowercase hex on purpose:
// the override for the brand pink matches the uppercase class name only.
const ACCENT_TEXT = "tw-text-[#ff43a7]";
const SURFACE = "tw-bg-[#22063F]";
const LINE = "tw-border-white/[0.12]";

// Made-up films, posters drawn with gradients.
const POSTERS = [
  { title: "Salt Lines", tagline: "A coastal thriller", colors: ["#0f3b57", "#0a1424"] },
  { title: "Northern Hours", tagline: "Drama", colors: ["#5b2a86", "#170a2b"] },
  { title: "Paper Kites", tagline: "Coming of age", colors: ["#b8476b", "#2a0d23"] },
  { title: "The Quiet Yard", tagline: "Documentary feature", colors: ["#3d5a3a", "#0e1a0d"] },
  { title: "Static Bloom", tagline: "Sci-fi", colors: ["#c46b2d", "#28100a"] },
];

// White EPK cards (awards, resources) use this pink-to-midnight border.
const CARD_BORDER = { background: "linear-gradient(180deg, #FF00A0, #1E0039)" };

// Made-up people for the actor results and the inbox.
const PEOPLE = [
  { name: "Maya Laurent", initials: "ML", color: "#7a3fb8" },
  { name: "Theo Garnier", initials: "TG", color: "#2f6f8f" },
  { name: "Inès Haddad", initials: "IH", color: "#a2456e" },
  { name: "Sam Okafor", initials: "SO", color: "#3f7a52" },
];

function MockPoster({ poster }) {
  const { t } = useTranslation();
  return (
    <div
      className="tw-flex tw-aspect-[2/3] tw-flex-col tw-justify-end tw-overflow-hidden tw-rounded-md tw-p-2"
      style={{ background: `linear-gradient(160deg, ${poster.colors[0]}, ${poster.colors[1]})` }}
    >
      <span className="tw-block tw-text-[11px] tw-font-extrabold tw-uppercase tw-leading-tight tw-tracking-wide tw-text-white sm:tw-text-xs">
        {poster.title}
      </span>
      <span className="tw-mt-0.5 tw-block tw-text-[9px] tw-text-white/70">{t(poster.tagline)}</span>
    </div>
  );
}

function MockAvatar({ person, size = "tw-h-10 tw-w-10", online = false }) {
  return (
    <span className="tw-relative tw-inline-flex tw-shrink-0">
      <span
        className={`tw-inline-flex ${size} tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-bold tw-text-white`}
        style={{ backgroundColor: person.color }}
      >
        {person.initials}
      </span>
      {online && (
        <span className="tw-absolute tw-bottom-0 tw-right-0 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-border-2 tw-border-solid tw-border-[#22063F] tw-bg-[#22c55e]" />
      )}
    </span>
  );
}

function Chip({ selected, children, icon }) {
  return (
    <span
      className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-py-1 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide ${
        selected ? "tw-border-white tw-bg-white tw-text-[#14002A]" : "tw-border-white/50 tw-text-white/70"
      }`}
    >
      {children}
      {icon && <FontAwesomeIcon icon={icon} className="tw-text-[10px]" aria-hidden="true" />}
    </span>
  );
}

// Wraps a mockup the way FeatureShots wraps a screenshot. The mockup is a
// picture of the UI, so assistive tech gets one label instead of fake controls.
function MockupFrame({ label, children }) {
  return (
    <div
      role="img"
      aria-label={label}
      className={`tw-w-full tw-max-w-[560px] tw-select-none tw-rounded-lg tw-border tw-border-solid ${LINE} ${SURFACE} tw-p-4`}
    >
      {children}
    </div>
  );
}

// Catalogue: production stage switch, type filters and a poster grid.
export function CatalogueMockup({ label }) {
  const { t } = useTranslation();
  const stages = ["Pre-Production", "Production", "Post-Production"];
  const types = ["Movie", "TV Show", "Web Series", "Documentary"];

  return (
    <MockupFrame label={label}>
      <div className="tw-grid tw-grid-cols-3 tw-overflow-hidden tw-rounded-full tw-bg-white/[0.08] tw-text-center tw-text-[11px] tw-font-semibold sm:tw-text-xs">
        {stages.map((stage, index) => (
          <span
            key={stage}
            className={`tw-truncate tw-px-2 tw-py-1.5 ${index === 2 ? "tw-rounded-full tw-bg-[#712CB0] tw-text-white" : "tw-text-white/70"}`}
          >
            {t(stage)}
          </span>
        ))}
      </div>
      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
        {types.map((type) => (
          <Chip key={type} icon={faPlus}>{t(type)}</Chip>
        ))}
        <Chip selected icon={faCheck}>{t("All EPKs")}</Chip>
      </div>
      <div className="tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-2 sm:tw-grid-cols-5">
        {POSTERS.map((poster, index) => (
          <div key={poster.title} className={index > 2 ? "tw-hidden sm:tw-block" : undefined}>
            <MockPoster poster={poster} />
          </div>
        ))}
      </div>
    </MockupFrame>
  );
}

// Talent search: EPKs/Actors switch, active filters and matching actors.
export function FilterMockup({ label }) {
  const { t } = useTranslation();

  return (
    <MockupFrame label={label}>
      <div className="tw-grid tw-grid-cols-2 tw-overflow-hidden tw-rounded-full tw-bg-white/[0.08] tw-text-center tw-text-xs tw-font-semibold">
        <span className="tw-px-2 tw-py-1.5 tw-text-white/70">{t("EPKs")}</span>
        <span className="tw-rounded-full tw-bg-[#712CB0] tw-px-2 tw-py-1.5 tw-text-white">{t("Actors")}</span>
      </div>
      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
        <Chip selected icon={faXmark}>{t("Male")}</Chip>
        <Chip selected icon={faXmark}>26-29</Chip>
        <Chip icon={faChevronDown}>{t("Ethnicity")}</Chip>
        <Chip icon={faChevronDown}>{t("Representation")}</Chip>
        <Chip selected icon={faXmark}>{t("Montreal")}</Chip>
        <Chip icon={faChevronDown}>{t("Country")}</Chip>
      </div>
      <ul className="tw-m-0 tw-mt-4 tw-grid tw-list-none tw-grid-cols-2 tw-gap-2 tw-p-0">
        {PEOPLE.map((person) => (
          <li key={person.name} className={`tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid ${LINE} tw-p-2`}>
            <MockAvatar person={person} size="tw-h-8 tw-w-8" />
            <span className="tw-min-w-0">
              <span className="tw-block tw-truncate tw-text-xs tw-font-semibold tw-text-white">{person.name}</span>
              <span className="tw-block tw-truncate tw-text-[10px] tw-text-white/60">{t("Actor")} · {t("Montreal")}</span>
            </span>
          </li>
        ))}
      </ul>
    </MockupFrame>
  );
}

// Messaging: conversation list beside an open thread with a filmmaker.
export function InboxMockup({ label }) {
  const { t } = useTranslation();
  const [active, ...others] = PEOPLE;
  const times = ["10:49", "09:12", "08:30"];

  return (
    <MockupFrame label={label}>
      <div className="tw-grid tw-gap-3 sm:tw-grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="tw-hidden sm:tw-block">
          <div className={`tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid ${LINE} tw-px-2 tw-py-1.5 tw-text-[11px] tw-text-white/50`}>
            <FontAwesomeIcon icon={faSearch} aria-hidden="true" />
            {t("Search...")}
          </div>
          <ul className="tw-m-0 tw-mt-2 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
            {[active, ...others.slice(0, 2)].map((person, index) => (
              <li
                key={person.name}
                className={`tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-p-1.5 ${index === 0 ? "tw-bg-white/[0.08]" : ""}`}
              >
                <MockAvatar person={person} size="tw-h-8 tw-w-8" online={index === 0} />
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-block tw-truncate tw-text-xs tw-font-semibold tw-text-white">{person.name}</span>
                  <span className="tw-block tw-truncate tw-text-[10px] tw-text-white/50">{t("Filmmaker")}</span>
                </span>
                <span className="tw-text-[10px] tw-text-white/40">{times[index]}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={`tw-flex tw-flex-col tw-rounded-md tw-border tw-border-solid ${LINE} tw-bg-[#14002A] tw-p-3`}>
          <div className={`tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-b tw-border-solid ${LINE} tw-pb-2`}>
            <MockAvatar person={active} size="tw-h-8 tw-w-8" online />
            <span>
              <span className="tw-block tw-text-xs tw-font-semibold tw-text-white">{active.name}</span>
              <span className={`tw-block tw-text-[10px] ${ACCENT_TEXT}`}>{t("Filmmaker")}</span>
            </span>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-2 tw-py-3 tw-text-xs tw-leading-snug">
            <p className="tw-m-0 tw-max-w-[85%] tw-self-end tw-rounded-xl tw-rounded-br-sm tw-bg-[#712CB0] tw-px-3 tw-py-2 tw-text-white">
              {t("We loved your EPK. Is the film available for our fall program?")}
            </p>
            <p className="tw-m-0 tw-max-w-[85%] tw-self-start tw-rounded-xl tw-rounded-bl-sm tw-bg-white/[0.08] tw-px-3 tw-py-2 tw-text-white">
              {t("Thank you! Yes, it is. I can send you a private screener link today.")}
            </p>
            <p className="tw-m-0 tw-max-w-[85%] tw-self-end tw-rounded-xl tw-rounded-br-sm tw-bg-[#712CB0] tw-px-3 tw-py-2 tw-text-white">
              {t("Perfect, our programming team will watch it this week.")}
            </p>
          </div>
          <div className={`tw-mt-auto tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid ${LINE} tw-px-3 tw-py-2 tw-text-[11px] tw-text-white/40`}>
            <span className="tw-flex-1">{t("Your message...")}</span>
            <FontAwesomeIcon icon={faPaperPlane} className={ACCENT_TEXT} aria-hidden="true" />
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

function MockTabs({ tabs }) {
  const { t } = useTranslation();
  return (
    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
      {tabs.map((tab, index) => (
        <span
          key={tab}
          className={`tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold ${
            index === 0 ? "tw-bg-[#ff43a7] tw-text-[#14002A]" : "tw-bg-white/[0.08] tw-text-white/70"
          }`}
        >
          {t(tab)}
        </span>
      ))}
    </div>
  );
}

// EPK media library: photo albums and the trailer in the video gallery.
export function MediaMockup({ label }) {
  const { t } = useTranslation();

  return (
    <MockupFrame label={label}>
      <div className={`tw-rounded-md tw-border tw-border-solid ${LINE} tw-p-3`}>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-text-sm tw-font-bold tw-text-white">{t("Photo Albums")}</span>
          <MockTabs tabs={["Posters", "Stills", "Behind The Scene", "Premieres"]} />
        </div>
        <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-2 sm:tw-grid-cols-4">
          {POSTERS.slice(0, 4).map((poster, index) => (
            <div key={poster.title} className={index > 2 ? "tw-hidden sm:tw-block" : undefined}>
              <MockPoster poster={poster} />
            </div>
          ))}
        </div>
      </div>

      <div className={`tw-mt-3 tw-rounded-md tw-border tw-border-solid ${LINE} tw-p-3`}>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-text-sm tw-font-bold tw-text-white">{t("Video Gallery")}</span>
          <MockTabs tabs={["Trailers", "Behind The Scene", "Interviews"]} />
        </div>
        <div
          className="tw-relative tw-mt-3 tw-flex tw-aspect-[16/9] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md sm:tw-w-2/3"
          style={{ background: `linear-gradient(135deg, ${POSTERS[1].colors[0]}, ${POSTERS[0].colors[1]})` }}
        >
          <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/60 tw-text-white">
            <FontAwesomeIcon icon={faPlay} className="tw-ml-0.5 tw-text-sm" aria-hidden="true" />
          </span>
          <span className="tw-absolute tw-bottom-2 tw-left-2 tw-flex tw-items-center tw-gap-2">
            <span className="tw-rounded tw-bg-[#ff43a7] tw-px-1.5 tw-py-0.5 tw-text-[9px] tw-font-bold tw-uppercase tw-text-[#14002A]">{t("Trailers")}</span>
            <span className="tw-text-xs tw-font-semibold tw-text-white">{t("Official Trailer")}</span>
          </span>
        </div>
      </div>
    </MockupFrame>
  );
}

// Award and review cards, white with the EPK's gradient border.
export function AwardMockup({ label }) {
  const { t } = useTranslation();
  const cards = [
    { heading: "Northern Lens Review", body: <p className="tw-m-0 tw-text-center tw-text-sm tw-italic tw-text-[#1E0039]">"{t("A tender, unforgettable portrait of coming home.")}"</p> },
    {
      heading: "Lakeshore Film Festival",
      body: (
        <p className="tw-m-0 tw-text-center tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-[#712CB0]">
          {t("Official Selection")} 2026
        </p>
      ),
    },
  ];

  return (
    <MockupFrame label={label}>
      <div className="tw-grid tw-grid-cols-2 tw-gap-3">
        {cards.map((card) => (
          <div key={card.heading} className="tw-rounded-2xl tw-p-[3px]" style={CARD_BORDER}>
            <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-gap-3 tw-rounded-[13px] tw-bg-white tw-px-3 tw-py-4">
              <span className="tw-text-center tw-text-sm tw-font-bold tw-leading-tight tw-text-[#1E0039]">{card.heading}</span>
              <FontAwesomeIcon icon={faAward} className="tw-text-3xl tw-text-[#E0B54A]" aria-hidden="true" />
              {card.body}
            </div>
          </div>
        ))}
      </div>
    </MockupFrame>
  );
}

// A resource request card: what the production needs, for how long, plus share buttons.
export function ResourceMockup({ label }) {
  const { t } = useTranslation();

  return (
    <MockupFrame label={label}>
      <div className="tw-rounded-2xl tw-p-[3px]" style={CARD_BORDER}>
        <div className="tw-grid tw-overflow-hidden tw-rounded-[13px] tw-bg-white sm:tw-grid-cols-2">
          <div
            className="tw-flex tw-min-h-[120px] tw-items-center tw-justify-center"
            style={{ background: `linear-gradient(160deg, ${POSTERS[3].colors[0]}, ${POSTERS[3].colors[1]})` }}
          >
            <FontAwesomeIcon icon={faTruck} className="tw-text-5xl tw-text-white/80" aria-hidden="true" />
          </div>
          <div className="tw-p-4">
            <span className="tw-block tw-text-base tw-font-extrabold tw-uppercase tw-leading-tight tw-text-[#490080]">
              {t("Vintage military truck")}
            </span>
            <span className="tw-mt-1 tw-block tw-text-[11px] tw-italic tw-text-[#712CB0]">
              (5 {t("days")} {t("of filming")})
            </span>
            <span className="tw-mt-2 tw-block tw-text-xs tw-leading-snug tw-text-[#490080]">
              {t("We need a 1970s military truck for the convoy scene in act two.")}
            </span>
            <span className="tw-mt-3 tw-flex tw-gap-1.5">
              {[faFacebookF, faWhatsapp, faLinkedinIn, faEnvelope, faLink].map((icon, index) => (
                <span key={index} className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-[#E5E7EB] tw-text-[10px] tw-text-[#490080]">
                  <FontAwesomeIcon icon={icon} aria-hidden="true" />
                </span>
              ))}
            </span>
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

// The "Recommend this profile" modal, mid-search with one person picked.
export function RecommendMockup({ label }) {
  const { t } = useTranslation();
  const results = [{ person: PEOPLE[1], role: "Producer", selected: true }];

  return (
    <MockupFrame label={label}>
      <div className="tw-relative tw-mx-auto tw-max-w-sm tw-rounded-2xl tw-border-2 tw-border-solid tw-border-[#ff43a7] tw-bg-[#14002A] tw-p-5">
        <span className="tw-absolute tw-right-3 tw-top-3 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/20 tw-text-xs tw-text-white">
          <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
        </span>
        <span className="tw-block tw-px-6 tw-text-center tw-text-base tw-font-bold tw-text-white">
          {t("Recommend this profile to someone")}
        </span>
        <span className="tw-mt-4 tw-block tw-rounded-xl tw-border tw-border-solid tw-border-[#ff43a7]/30 tw-px-3 tw-py-2.5 tw-text-sm tw-text-white">
          {PEOPLE[1].name.split(" ")[0]}
        </span>
        <ul className="tw-m-0 tw-mt-2 tw-list-none tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-[#ff43a7]/20 tw-p-0">
          {results.map(({ person, role, selected }) => (
            <li key={person.name} className={`tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-3 tw-py-2 ${selected ? "tw-bg-[#ff43a7]/20" : ""}`}>
              <span className="tw-min-w-0">
                <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-white">{person.name}</span>
                <span className="tw-block tw-text-[10px] tw-uppercase tw-text-white/60">{t(role)}</span>
              </span>
              <MockAvatar person={person} size="tw-h-8 tw-w-8" />
            </li>
          ))}
        </ul>
        <span className="tw-mt-4 tw-block tw-rounded-xl tw-bg-[#ff43a7] tw-py-2.5 tw-text-center tw-text-sm tw-font-bold tw-text-[#570033]">
          {t("Send Recommendations")}
        </span>
      </div>
    </MockupFrame>
  );
}
